import styled from '@emotion/styled';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import Box from '~/common/components/Box';
import Spinner from '~/common/components/Spinner';
import { logError, logWarning } from '~/common/util/logger';
import ErrorBoundary from '~/pages/Home/ErrorBoundary';
import { DocumentTypes } from '~/pages/IncomeCalculator/enums';
import { useSelectedAlert } from '~/stores/navigationStore';

import YAMLOops from '../Oops/OopsComponent';
import Viewer from './Viewer';
import pdfViewerLanguage from './language.json';
import type { Citation, DocumentOption } from './types';
import { useGetPDFUrl } from './useGetPDFUrl';

const StyledViewerContainer = styled<any>(Box)`
  width: 100%;
  height: 100%;
  border-right: 1px solid ${(props) => props.theme.colors?.gray[100]};
`;

type PDFViewerProps = {
  defaultDocumentId?: number | string;
  defaultPage?: number | string;
  isFullPage?: boolean;
  documentOptions?: Array<DocumentOption>;
};

const documentTypesToResetCitation: Set<string> = new Set([DocumentTypes.W2]);

function toValidPageNumber(page?: number | string): number | undefined {
  if (page == null || page === '') return undefined;
  const parsed = Math.floor(Number(page));
  if (!Number.isFinite(parsed) || parsed < 1) return undefined;
  return parsed;
}

/**
 * @param {Object} params - The parameters for the PDFViewer.
 * @param {string} params.defaultDocumentId - The ID of the default document to be displayed.
 * @param {number|string} [params.defaultPage] - 1-based page to open when the PDF loads. Ignored when a citation is active.
 * @param {boolean} [params.isFullPage=false] - Flag indicating if the viewer should be displayed in full page mode.
 * @param {{id:number;label:string;}[]} [params.documentOptions=[]] - An array of documents to show in the selection dropdown.
 *
 * Note: If you are building a YAML, `documentOptions` will be overridden by the value passed to the App Renderer via the `data` prop named `documentOptions`.
 *
 * @example // Do this if you are using YAML
 * const screenData = { documentOptions: [ { id: 1, label: "Document To Select"} ] }
 * <AppRenderer data={screenData} />
 */
function PDFViewer({
  defaultDocumentId,
  defaultPage,
  isFullPage = false,
  documentOptions = [],
}: Readonly<PDFViewerProps>) {
  const [currentDocId, setCurrentDocId] = useState<number>(
    defaultDocumentId ? +defaultDocumentId : documentOptions?.[0]?.id,
  );
  const [activeCitation, setActiveCitation] = useState<Citation>(null);
  const isMountedRef = useRef(true);

  const { data: url, isLoading, isError } = useGetPDFUrl(currentDocId);

  const handlePDFCitationMessage = useCallback(async (event: MessageEvent) => {
    try {
      if (
        !(
          (event.data?.type === 'pdfCitation' && event.data.payload?.length) ||
          event.data.payload?.boundingBoxes?.length
        )
      )
        return;

      const citation = event.data.payload[0] || event.data.payload;
      if (!isMountedRef.current) {
        logWarning('Attempted to set active citation on unmounted component');
        return;
      }
      if (citation?.documentId && citation?.boundingBoxes?.length !== 0) {
        setCurrentDocId(citation.documentId);
      }
      setActiveCitation(citation?.boundingBoxes?.length ? citation : null);
    } catch (error) {
      logError('Error handling PDF citation message:', error);
    }
  }, []);

  const selectedAlert = useSelectedAlert();
  const documentTypeId =
    selectedAlert?.additionalReferences?.DocumentTypeId ?? '';

  function resetActiveCitationIfNeeded() {
    if (documentTypesToResetCitation.has(documentTypeId)) {
      setActiveCitation(null);
    }
  }

  useEffect(() => {
    resetActiveCitationIfNeeded();
    if (!documentOptions.length) {
      setCurrentDocId(+defaultDocumentId);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [currentDocId, defaultDocumentId, documentOptions.length]);

  useEffect(() => {
    if (!documentOptions.length) return;

    setCurrentDocId((documentId) =>
      documentOptions.some((option) => option.id === documentId)
        ? documentId
        : documentOptions[0].id,
    );
    setActiveCitation((citation) =>
      citation?.documentId &&
      !documentOptions.some((option) => option.id === citation.documentId)
        ? null
        : citation,
    );
  }, [documentOptions]);

  useEffect(() => {
    isMountedRef.current = true;

    window.addEventListener('message', handlePDFCitationMessage);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('message', handlePDFCitationMessage);
    };
  }, [handlePDFCitationMessage]);

  const hasDocumentOrDefault = !!url || url === 'noDocumentFound';

  const isReady = !isError && !isLoading && hasDocumentOrDefault;
  const resolvedDefaultPage = toValidPageNumber(defaultPage);
  const citationPageNumber = activeCitation?.boundingBoxes?.length
    ? activeCitation.boundingBoxes[0]?.pageNumber
    : undefined;

  return (
    <ErrorBoundary
      fallback={
        <YAMLOops
          header={pdfViewerLanguage.ERROR_HEADER}
          message={pdfViewerLanguage.ERROR_MESSAGE}
        />
      }
    >
      <StyledViewerContainer>
        {isError && <YAMLOops />}
        {isLoading && <Spinner />}
        {isReady && (
          <Viewer
            docname={activeCitation?.documentType}
            documentId={currentDocId}
            pdf={url}
            highlights={[]}
            citations={activeCitation?.boundingBoxes}
            pageNumber={citationPageNumber ?? resolvedDefaultPage ?? 0}
            pageRotations={null}
            isFullPage={isFullPage}
            documentOptions={documentOptions}
            showHighLight={activeCitation?.showHighlight}
          />
        )}
      </StyledViewerContainer>
    </ErrorBoundary>
  );
}

const MemoizedPDFViewer = memo(PDFViewer);

export default MemoizedPDFViewer;
